import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import ToastService from '~/services/toast.service';

import { queryKeysInvoiceReprocessing } from '../queryKeys';

import { type UpdateCurrentParsedInvoiceMutationArguments } from './types';
import { updateCurrentParsedInvoice } from './updateCurrentParsedInvoice';

/**
 * A custom hook to update the data of the current parsed invoice for a reprocessing invoice with a given process ID.
 * Uses the `useMutation` hook from `@tanstack/react-query` internally.
 *
 * @param options - Optional configuration for the mutation
 * @returns The mutation result.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/update_current_parsed_invoice_invoice_process__process_id__current_parsed_invoice_put
 */
export const useMutationUpdateCurrentParsedInvoice = (
  options?: UseMutationOptions<
    void,
    unknown,
    UpdateCurrentParsedInvoiceMutationArguments
  >,
) => {
  const { t } = useTranslation('invoiceReprocessing');

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ parsedInvoiceData, processId }) =>
      updateCurrentParsedInvoice({
        parsedInvoiceData,
        processId,
      }),
    onError(_error) {
      return; // prevent error from bubbling up further unless overridden via mutation options
    },
    async onSettled(_data, _error, { processId }) {
      // Refetch the list of invoice processes
      void queryClient.invalidateQueries({
        queryKey: queryKeysInvoiceReprocessing.getAll({}),
      });

      // Refetch the invoice process we just updated
      // Await refetch so that we can reset the form with the new data.
      await queryClient.invalidateQueries({
        queryKey: queryKeysInvoiceReprocessing.get(processId),
      });
    },
    onSuccess() {
      ToastService.success(
        t(($) => $.processEdit.mutation.updateCurrentParsedInvoice.success),
      );
    },
    ...options,
  });
};
